import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { doc, onSnapshot, collection, query, orderBy, setDoc, serverTimestamp, writeBatch, getDocs } from 'firebase/firestore';
import { fn } from '../api/functions';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { QuestionList } from '../components/QuestionList';
import { StoryHeader } from '../components/StoryHeader';
import { AudioRecorder } from '../components/AudioRecorder';
import AIInterviewer from '../components/AIInterviewer';
import SmartAIQuestionGenerator from '../components/SmartAIQuestionGenerator_v2';
import PDFGenerator from '../components/PDFGenerator';
import { SparklesIcon, PlusIcon, TrashIcon, PencilIcon, ChevronUpIcon, ChevronDownIcon, Bars3Icon, ClockIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import { v4 as uuidv4 } from 'uuid';

export const StoryEditorPage = () => {
  const { storyId } = useParams();
  const { currentUser } = useAuth();
  const [story, setStory] = useState(null);
  const [userAccessLevel, setUserAccessLevel] = useState('owner'); // owner, editor, viewer
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedQuestionAnswers, setSelectedQuestionAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [followupMode, setFollowupMode] = useState(false);
  const [showSmartQuestionWizard, setShowSmartQuestionWizard] = useState(false);
  const [followupData, setFollowupData] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(false);
  const [editQuestionText, setEditQuestionText] = useState('');
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [pdfGenerationModal, setPdfGenerationModal] = useState({ isOpen: false, storyId: null, storyTitle: null });

  useEffect(() => {
    if (!storyId || !currentUser) return;

    // Listen to story changes
    const storyRef = doc(db, 'stories', storyId);
    const unsubscribeStory = onSnapshot(storyRef, (doc) => {
      if (doc.exists()) {
        const storyData = { id: doc.id, ...doc.data() };
        setStory(storyData);
        
        // Determine user's access level
        if (storyData.userId === currentUser.uid) {
          setUserAccessLevel('owner');
        } else {
          // Check if user is the gift giver (they should have editor access)
          const isGifter = storyData.giftAccess?.gifter?.userId === currentUser.uid || 
                          storyData.giftAccess?.gifter?.email === currentUser.email;
          
          // Check if user is the gift recipient (before transfer)
          const isRecipient = storyData.giftAccess?.recipientEmail === currentUser.email;

          if (isGifter || isRecipient) {
            setUserAccessLevel('editor');
          } else {
            // Check if user has shared access
            const sharedAccess = storyData.sharedAccess?.find(
              access => access.userId === currentUser.uid || access.userEmail === currentUser.email
            );
            if (sharedAccess) {
              setUserAccessLevel(sharedAccess.accessLevel); // 'viewer' or 'editor'
            } else {
              setUserAccessLevel('viewer'); // Default to viewer if somehow they got here
            }
          }
        }
      }
      setLoading(false);
    });

    // Listen to questions changes
    const questionsRef = collection(db, 'stories', storyId, 'questions');
    const q = query(questionsRef, orderBy('order'));
    const unsubscribeQuestions = onSnapshot(q, (snapshot) => {
      const questionList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuestions(questionList);
      
      // Auto-selection disabled for better mobile UX and senior user clarity
      // Users should explicitly choose which question to answer
      // if (!selectedQuestion && questionList.length > 0) {
      //   const firstUnanswered = questionList.find(q => !q.hasAnswer);
      //   handleSelectQuestion(firstUnanswered || questionList[0]);
      // }
    });

    return () => {
      unsubscribeStory();
      unsubscribeQuestions();
    };
  }, [storyId, currentUser, selectedQuestion]);

  const handleAnswerComplete = async ({ transcription, questionId, storyId }) => {
    // Add a delay to allow Firestore write to propagate before reading
    // This prevents the race condition where we read before write is committed
    setTimeout(async () => {
      await loadQuestionAnswers(questionId);
    }, 1000); // 1 second delay to ensure Firestore consistency
    
    // Don't auto-advance to next question anymore - let user choose when to move on
    // This allows them to see their transcription and potentially record follow-ups
  };

  // Follow-up handler for AI interviews
  const handleStartFollowup = ({ transcription, questionId, storyId, question }) => {
    setFollowupData({
      transcription,
      questionId,
      storyId,
      question,
      answerId: `answer_${Date.now()}` // Generate a temporary answer ID
    });
    setFollowupMode(true);
  };

  // Load answers for a selected question
  const loadQuestionAnswers = async (questionId) => {
    if (!questionId) {
      setSelectedQuestionAnswers([]);
      return;
    }

    try {
      const answersQuery = query(
        collection(db, 'stories', storyId, 'questions', questionId, 'answers'),
        orderBy('createdAt')
      );
      const answersSnapshot = await getDocs(answersQuery);
      const answers = answersSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      
      setSelectedQuestionAnswers(answers);
    } catch (error) {
      console.error('Error loading question answers:', error);
      setSelectedQuestionAnswers([]);
    }
  };

  // Handle question selection with answer loading
  const handleSelectQuestion = async (question) => {
    setSelectedQuestion(question);
    await loadQuestionAnswers(question?.id);
  };

  const handleFollowupComplete = () => {
    setFollowupMode(false);
    setFollowupData(null);
    // Optionally auto-select next question
    const currentIndex = questions.findIndex(q => q.id === followupData?.questionId);
    const nextQuestion = questions.find((q, index) => index > currentIndex && !q.hasAnswer);
    
    if (nextQuestion) {
      handleSelectQuestion(nextQuestion);
    }
  };

  const handleDeleteQuestion = async (questionToDelete) => {
    if (!questionToDelete || !currentUser) return;

    // Confirm deletion with enhanced warning
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this question?\n\n"${questionToDelete.text}"\n\nThis will permanently delete the question, all its answers, and associated audio files. This action cannot be undone.`
    );
    
    if (!confirmDelete) return;

    try {
      // Use the comprehensive deletion function
      const result = await fn.deleteQuestionCompletely({ 
        storyId: storyId, 
        questionId: questionToDelete.id 
      });
      
      console.log('Deletion result:', result.data);
      
      // If the deleted question was selected, select another question
      if (selectedQuestion && selectedQuestion.id === questionToDelete.id) {
        const remainingQuestions = questions.filter(q => q.id !== questionToDelete.id);
        if (remainingQuestions.length > 0) {
          // Select the next question in the list, or the first one if we deleted the last
          const currentIndex = questions.findIndex(q => q.id === questionToDelete.id);
          const nextQuestion = remainingQuestions[currentIndex] || remainingQuestions[currentIndex - 1] || remainingQuestions[0];
          await handleSelectQuestion(nextQuestion);
        } else {
          // No questions left
          setSelectedQuestion(null);
          setSelectedQuestionAnswers([]);
        }
      }
      
      // Show success message with deletion stats
      const photoCount = result.data.deletedPhotos || 0;
      alert(`Question deleted successfully. Removed ${result.data.deletedAnswers} answers, ${result.data.deletedAudioFiles} audio files, and ${photoCount} photos.`);
      
    } catch (error) {
      console.error('Error deleting question completely:', error);
      alert('Failed to delete question completely. Please try again.');
    }
  };

  const generateMoreQuestions = async () => {
    // Block demo stories from generating more questions
    if (story?.isDemo) {
      alert('🎯 Demo stories are limited to the initial question.\n\nTo generate personalized questions and create your full story, please sign up for Memory Stream!');
      return;
    }
    
    // Show the Smart Question Generator wizard instead of directly generating
    setShowSmartQuestionWizard(true);
  };

  // Handler for when questions are updated from the wizard
  const handleQuestionsUpdate = async (newQuestions) => {
    if (!newQuestions || newQuestions.length === 0) return;

    try {
      // Add new questions to Firestore
      const questionsCollection = collection(db, 'stories', storyId, 'questions');
      const nextOrder = Math.max(...questions.map(q => q.order || 0)) + 1;
      
      for (let i = 0; i < newQuestions.length; i++) {
        const questionText = newQuestions[i];
        const questionId = uuidv4();
        await setDoc(doc(db, 'stories', storyId, 'questions', questionId), {
          id: questionId,
          text: questionText,
          order: nextOrder + i,
          storyId: storyId,
          userId: currentUser.uid,
          createdAt: serverTimestamp(),
          hasAnswer: false
        });
      }

      // Close the wizard
      setShowSmartQuestionWizard(false);
      alert(`Added ${newQuestions.length} new questions to your story!`);
      
    } catch (error) {
      console.error('Error adding questions:', error);
      alert('Failed to add questions. Please try again.');
    }
  };

  const generatePDF = async () => {
    if (!story) return;
    // Open the PDF generation modal
    setPdfGenerationModal({ isOpen: true, storyId: story.id, storyTitle: story.title });
  };

  const handleEditQuestion = () => {
    setEditingQuestion(true);
    setEditQuestionText(selectedQuestion.text);
  };

  const handleSaveQuestion = async () => {
    if (!editQuestionText.trim() || !selectedQuestion) return;

    setSavingQuestion(true);
    try {
      const questionRef = doc(db, 'stories', storyId, 'questions', selectedQuestion.id);
      await setDoc(questionRef, {
        ...selectedQuestion,
        text: editQuestionText.trim(),
        updatedAt: serverTimestamp()
      });

      // Update local state
      setSelectedQuestion(prev => ({ ...prev, text: editQuestionText.trim() }));
      setEditingQuestion(false);
      setEditQuestionText('');
    } catch (error) {
      console.error('Error updating question:', error);
      alert('Failed to update question. Please try again.');
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleCancelEditQuestion = () => {
    setEditingQuestion(false);
    setEditQuestionText('');
  };

  const addCustomQuestion = async () => {
    // Block demo stories from adding custom questions
    if (story?.isDemo) {
      alert('🎯 Demo stories are limited to the initial question.\n\nTo add custom questions and create your full story, please sign up for Memory Stream!');
      return;
    }
    
    const customQuestion = prompt("Enter your custom question:");
    
    if (!customQuestion || !customQuestion.trim()) {
      return; // User cancelled or entered empty question
    }

    try {
      // Add new question to Firestore
      const questionsCollection = collection(db, 'stories', storyId, 'questions');
      const nextOrder = Math.max(...questions.map(q => q.order || 0)) + 1;
      const questionId = uuidv4();
      
      await setDoc(doc(db, 'stories', storyId, 'questions', questionId), {
        id: questionId,
        text: customQuestion.trim(),
        order: nextOrder,
        storyId: storyId,
        userId: currentUser.uid,
        createdAt: serverTimestamp(),
        hasAnswer: false,
        manuallyAdded: true // Flag to indicate this was manually added
      });

      alert('Custom question added successfully!');
      
    } catch (error) {
      console.error('Error adding custom question:', error);
      alert('Failed to add custom question. Please try again.');
    }
  };

  const getStoryStats = () => {
    const total = questions.length;
    const answered = questions.filter(q => q.hasAnswer).length;
    const percentage = total > 0 ? Math.round((answered / total) * 100) : 0;
    
    return { total, answered, percentage };
  };

  const handleMoveQuestion = async (questionId, direction) => {
    const currentIndex = questions.findIndex(q => q.id === questionId);
    if (currentIndex === -1) return;

    let newIndex;
    if (direction === 'up') {
      if (currentIndex === 0) return; // Can't move up from first position
      newIndex = currentIndex - 1;
    } else {
      if (currentIndex === questions.length - 1) return; // Can't move down from last position
      newIndex = currentIndex + 1;
    }

    setReordering(true);
    
    try {
      // Direct Firestore update to avoid CORS issues
      const batch = writeBatch(db);
      
      // Remove the question from its current position
      const questionsToReorder = [...questions];
      const questionToMove = questionsToReorder[currentIndex];
      questionsToReorder.splice(currentIndex, 1);
      
      // Insert it at the new position
      questionsToReorder.splice(newIndex, 0, questionToMove);
      
      // Update order values for all questions
      questionsToReorder.forEach((question, index) => {
        const questionRef = doc(db, 'stories', storyId, 'questions', question.id);
        batch.update(questionRef, { order: index });
      });
      
      await batch.commit();
      
      // Update local state immediately for better UX
      setQuestions(questionsToReorder);
    } catch (error) {
      console.error('Error reordering questions:', error);
      alert('Failed to reorder questions. Please try again.');
    } finally {
      setReordering(false);
    }
  };

  const toggleReorderMode = () => {
    setReorderMode(!reorderMode);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-medium text-gray-900">Story not found</h2>
      </div>
    );
  }

  const stats = getStoryStats();

  return (
    <>
    <div className="w-full lg:max-w-6xl lg:mx-auto">
      {/* Hide StoryHeader on mobile when question is selected */}
      <div className={selectedQuestion ? 'hidden md:block' : 'block'}>
        <StoryHeader story={story} />
      </div>
      
      {/* Story Progress and Actions - Hidden when answering questions on mobile/tablet */}
      {/* Hide entire section for demo stories */}
      {!story?.isDemo && (
      <div className={`bg-white lg:rounded-lg lg:shadow-sm lg:border lg:border-gray-200 lg:p-6 lg:mt-6 p-4 mt-4 border-t border-b border-gray-200 ${selectedQuestion ? 'lg:block hidden' : 'block'}`}>
        {/* Mobile + Tablet Layout: Stacked */}
        <div className="block lg:hidden">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Story Progress</h3>
              <p className="text-sm text-gray-600">
                {stats.answered} of {stats.total} questions answered ({stats.percentage}%)
              </p>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full">
              <div className="bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${stats.percentage}%` }}
                ></div>
              </div>
            </div>

            {/* Action Buttons - Full width on mobile */}
            {userAccessLevel === 'viewer' ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 text-center">
                  👁️ <strong>Viewer Access:</strong> You can read and listen to this story, but cannot edit or add questions.
                </p>
              </div>
            ) : (
              <div className="flex flex-col space-y-3">
                <button
                  onClick={generateMoreQuestions}
                  disabled={generatingQuestions || stats.answered < 2}
                  className="memory-stream-button-secondary flex items-center justify-center w-full"
                  title={stats.answered < 2 ? "Answer at least 2 questions to generate smart follow-ups" : "Generate personalized follow-up questions"}
                >
                  <SparklesIcon className="h-4 w-4 mr-2" />
                  {generatingQuestions ? 'Generating...' : 'Smart Questions'}
                </button>
                
                <button
                  onClick={addCustomQuestion}
                  className="memory-stream-button-secondary flex items-center justify-center w-full"
                  title="Add your own custom question"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Custom Question
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Large Desktop Layout: Side by side */}
        <div className="hidden lg:block">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Story Progress</h3>
                <p className="text-sm text-gray-600">
                  {stats.answered} of {stats.total} questions answered ({stats.percentage}%)
                </p>
              </div>
              
              {/* Progress Bar */}
              <div className="w-48">
                <div className="bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${stats.percentage}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {userAccessLevel === 'viewer' ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                <p className="text-sm text-blue-800">
                  👁️ <strong>Viewer Access:</strong> Read-only mode
                </p>
              </div>
            ) : (
              <div className="flex space-x-3">
                <button
                  onClick={generateMoreQuestions}
                  disabled={generatingQuestions || stats.answered < 2}
                  className="memory-stream-button-secondary flex items-center"
                  title={stats.answered < 2 ? "Answer at least 2 questions to generate smart follow-ups" : "Generate personalized follow-up questions"}
                >
                  <SparklesIcon className="h-4 w-4 mr-2" />
                  {generatingQuestions ? 'Generating...' : 'Smart Questions'}
                </button>
                
                <button
                  onClick={addCustomQuestion}
                  className="memory-stream-button-secondary flex items-center"
                  title="Add your own custom question"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Custom Question
                </button>
              </div>
            )}
          </div>
        </div>

        {stats.answered >= 5 && (
          <div className="mt-4 p-3 bg-green-50 rounded-md">
            <p className="text-sm text-green-800">
              🎉 Great progress! You've answered {stats.answered} questions. 
              Try the "Smart Questions" feature to generate personalized follow-ups based on your responses.
            </p>
          </div>
        )}
      </div>
      )}
      
      {/* Mobile-First Responsive Layout */}
      <div className="mt-8">
        {/* Mobile + Tablet View: Show either questions list OR selected question */}
        <div className="lg:hidden px-2">
          {!selectedQuestion ? (
            /* Mobile Questions List */
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Your Questions</h2>
                <button
                  onClick={toggleReorderMode}
                  disabled={reordering || questions.length < 2}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                    reorderMode
                      ? 'bg-red-100 text-red-700 border border-red-300 hover:bg-red-200'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  } ${
                    questions.length < 2 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title={questions.length < 2 ? 'Need at least 2 questions to reorder' : ''}
                >
                  <Bars3Icon className="h-4 w-4" />
                  <span>
                    {reordering ? 'Reordering...' : reorderMode ? 'Cancel' : 'Reorder'}
                  </span>
                </button>
              </div>

              {reorderMode && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Reorder Mode:</strong> Use the up/down arrows to rearrange questions. 
                    All answers and photos will stay with their questions.
                  </p>
                </div>
              )}

              {questions.map((question, index) => (
                <div key={question.id} className="relative">
                  {reorderMode ? (
                    <div className="w-full bg-white lg:rounded-lg lg:shadow-sm lg:border lg:border-gray-200 p-6 border-b border-gray-200">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          {question.hasAnswer ? (
                            <div className="h-6 w-6 bg-green-500 rounded-full flex items-center justify-center">
                              <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ) : (
                            <div className="h-6 w-6 bg-gray-300 rounded-full"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-lg font-semibold text-gray-900">
                              Question {index + 1}
                            </span>
                            <div className="flex items-center space-x-2">
                              {question.hasAnswer && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Answered
                                </span>
                              )}
                              <div className="flex flex-col">
                                <button
                                  onClick={() => handleMoveQuestion(question.id, 'up')}
                                  disabled={index === 0 || reordering}
                                  className={`p-1 rounded transition-colors ${
                                    index === 0 || reordering
                                      ? 'text-gray-300 cursor-not-allowed'
                                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                                  }`}
                                  title="Move up"
                                >
                                  <ChevronUpIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleMoveQuestion(question.id, 'down')}
                                  disabled={index === questions.length - 1 || reordering}
                                  className={`p-1 rounded transition-colors ${
                                    index === questions.length - 1 || reordering
                                      ? 'text-gray-300 cursor-not-allowed'
                                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                                  }`}
                                  title="Move down"
                                >
                                  <ChevronDownIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                          <p className="text-lg md:text-xl text-gray-900 leading-relaxed mb-1 line-clamp-3 font-normal">
                            {question.text}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleSelectQuestion(question)}
                      className="w-full bg-white lg:rounded-lg lg:shadow-sm lg:border lg:border-gray-200 lg:hover:shadow-md p-6 text-left border-b border-gray-200 transition-shadow duration-200"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          {question.hasAnswer ? (
                            <div className="h-6 w-6 bg-green-500 rounded-full flex items-center justify-center">
                              <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ) : (
                            <div className="h-6 w-6 bg-gray-300 rounded-full"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-lg font-semibold text-gray-900">
                              Question {index + 1}
                            </span>
                            <div className="flex items-center space-x-2">
                              {question.hasAnswer && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Answered
                                </span>
                              )}
                            </div>
                          </div>
                          <p className="text-lg md:text-xl text-gray-900 leading-relaxed mb-1 line-clamp-3 font-normal">
                            {question.text}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* Mobile Question Detail */
            <div className="space-y-6">
              {/* Back Button */}
              <button
                onClick={() => setSelectedQuestion(null)}
                className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Questions
              </button>

              {/* Question Card */}
              <div className="memory-stream-card p-4 md:p-6">
                <div className="flex items-start justify-between mb-4 gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 leading-tight">
                      Question {questions.findIndex(q => q.id === selectedQuestion.id) + 1}
                    </h3>
                    {selectedQuestion.hasAnswer && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                        Answered
                      </span>
                    )}
                  </div>
                  {userAccessLevel !== 'viewer' && (
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <button
                        onClick={handleEditQuestion}
                        className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                        title="Edit question"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(selectedQuestion)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Delete this question"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Question Text - Editable */}
                <div className="mb-6">
                  {editingQuestion ? (
                    <div className="space-y-4">
                      <textarea
                        value={editQuestionText}
                        onChange={(e) => setEditQuestionText(e.target.value)}
                        className="w-full p-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xl font-semibold text-gray-900 min-h-[120px] resize-none text-center"
                        placeholder="Edit your question..."
                        disabled={savingQuestion}
                      />
                      <div className="flex flex-col space-y-3">
                        <button
                          onClick={handleSaveQuestion}
                          disabled={savingQuestion || !editQuestionText.trim()}
                          className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                        >
                          <span>{savingQuestion ? 'Saving...' : 'Save Question'}</span>
                        </button>
                        <button
                          onClick={handleCancelEditQuestion}
                          disabled={savingQuestion}
                          className="flex items-center justify-center space-x-2 px-4 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50 transition-colors font-medium"
                        >
                          <span>Cancel</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="font-heading text-gray-900 text-2xl md:text-3xl lg:text-4xl leading-relaxed font-semibold text-center">
                      {selectedQuestion.text}
                    </p>
                  )}
                </div>
                
                <AudioRecorder
                  storyId={storyId}
                  questionId={selectedQuestion.id}
                  question={selectedQuestion}
                  existingAnswers={selectedQuestionAnswers}
                  onAnswerComplete={handleAnswerComplete}
                  onStartFollowup={handleStartFollowup}
                  useSteppedInterface={true}
                  userAccessLevel={userAccessLevel}
                  story={story}
                />
              </div>
            </div>
          )}
        </div>

        {/* Large Desktop View: Side-by-side layout */}
        {!followupMode && (
        <div className="hidden lg:block" style={{ isolation: 'auto' }}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Questions List - Scrollable */}
            <div className="lg:col-span-1 relative" style={{ zIndex: 0, isolation: 'auto' }}>
              <div className="memory-stream-card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Questions</h2>
                  <button
                    onClick={toggleReorderMode}
                    disabled={reordering || questions.length < 2}
                    className={`flex items-center space-x-1 px-2 py-1 text-xs rounded-md font-medium transition-colors ${
                      reorderMode
                        ? 'bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-200'
                        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                    } ${
                      questions.length < 2 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    title={questions.length < 2 ? 'Need at least 2 questions to reorder' : ''}
                  >
                    <Bars3Icon className="h-3 w-3" />
                    <span>
                      {reordering ? 'Reordering...' : reorderMode ? 'Exit' : 'Reorder'}
                    </span>
                  </button>
                </div>

                {reorderMode && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-800">
                      <strong>Reorder Mode:</strong> Use the up/down arrows to rearrange questions. 
                      All answers and photos stay with their questions.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  {questions.map((question, index) => (
                    <div key={question.id} className="relative">
                      {reorderMode ? (
                        <div className={`relative rounded-md transition-colors duration-200 ${
                          selectedQuestion?.id === question.id
                            ? 'bg-blue-50 border-2 border-blue-200'
                            : 'bg-gray-50 border-2 border-transparent'
                        }`}>
                          <div className="w-full text-left p-3">
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-3 mt-0.5">
                                {question.hasAnswer ? (
                                  <CheckCircleIconSolid className="h-5 w-5 text-green-500" />
                                ) : (
                                  <ClockIcon className="h-5 w-5 text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-gray-900">
                                    Question {index + 1}
                                  </span>
                                  <div className="flex items-center space-x-2">
                                    {question.hasAnswer && (
                                      <span className="text-xs text-green-600 font-medium">
                                        Answered
                                      </span>
                                    )}
                                    <div className="flex flex-col">
                                      <button
                                        onClick={() => handleMoveQuestion(question.id, 'up')}
                                        disabled={index === 0 || reordering}
                                        className={`p-0.5 rounded transition-colors ${
                                          index === 0 || reordering
                                            ? 'text-gray-300 cursor-not-allowed'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                                        }`}
                                        title="Move up"
                                      >
                                        <ChevronUpIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => handleMoveQuestion(question.id, 'down')}
                                        disabled={index === questions.length - 1 || reordering}
                                        className={`p-0.5 rounded transition-colors ${
                                          index === questions.length - 1 || reordering
                                            ? 'text-gray-300 cursor-not-allowed'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                                        }`}
                                        title="Move down"
                                      >
                                        <ChevronDownIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <p className="text-base text-gray-600 line-clamp-3 leading-relaxed">
                                  {question.text}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleSelectQuestion(question)}
                          className={`w-full text-left p-3 relative rounded-md transition-colors duration-200 ${
                            selectedQuestion?.id === question.id
                              ? 'bg-blue-50 border-2 border-blue-200'
                              : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                          }`}
                        >
                          <div className="flex items-start">
                            <div className="flex-shrink-0 mr-3 mt-0.5">
                              {question.hasAnswer ? (
                                <CheckCircleIconSolid className="h-5 w-5 text-green-500" />
                              ) : (
                                <ClockIcon className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-900">
                                  Question {index + 1}
                                </span>
                                <div className="flex items-center space-x-2">
                                  {question.hasAnswer && (
                                    <span className="text-xs text-green-600 font-medium">
                                      Answered
                                    </span>
                                  )}
                                </div>
                              </div>
                              <p className="text-base text-gray-600 line-clamp-3 leading-relaxed">
                                {question.text}
                              </p>
                            </div>
                          </div>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>Progress</span>
                    <span>
                      {questions.filter(q => q.hasAnswer).length} of {questions.length}
                    </span>
                  </div>
                  <div className="mt-2 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${questions.length > 0 ? (questions.filter(q => q.hasAnswer).length / questions.length) * 100 : 0}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Question Detail & Recording - Sticky */}
            <div className="lg:col-span-2 sticky top-6 z-10 self-start max-h-[calc(100vh-3rem)] overflow-y-auto">
              {selectedQuestion ? (
                <div className="memory-stream-card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Question {questions.findIndex(q => q.id === selectedQuestion.id) + 1}
                    </h3>
                    <div className="flex items-center space-x-3">
                      {selectedQuestion.hasAnswer && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Answered
                        </span>
                      )}
                      <button
                        onClick={() => handleDeleteQuestion(selectedQuestion)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Delete this question"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-4 mb-6">
                    {editingQuestion ? (
                      <div className="space-y-4">
                        <textarea
                          value={editQuestionText}
                          onChange={(e) => setEditQuestionText(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-medium text-gray-900 min-h-[100px] resize-none"
                          placeholder="Edit your question..."
                          disabled={savingQuestion}
                        />
                        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
                          <button
                            onClick={handleSaveQuestion}
                            disabled={savingQuestion || !editQuestionText.trim()}
                            className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <span>{savingQuestion ? 'Saving...' : 'Save Question'}</span>
                          </button>
                          <button
                            onClick={handleCancelEditQuestion}
                            disabled={savingQuestion}
                            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50 transition-colors"
                          >
                            <span>Cancel</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h2 className="font-heading text-2xl md:text-3xl text-gray-900 mb-3 leading-tight font-semibold">
                            {selectedQuestion.text}
                          </h2>
                          <p className="text-sm text-blue-700 opacity-75">
                            Question {questions.findIndex(q => q.id === selectedQuestion.id) + 1} of {questions.length}
                          </p>
                        </div>
                        <button
                          onClick={handleEditQuestion}
                          className="flex-shrink-0 ml-3 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                          title="Edit question"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Single Author Recording Interface */}
                  <AudioRecorder
                    storyId={storyId}
                    questionId={selectedQuestion.id}
                    question={selectedQuestion}
                    existingAnswers={selectedQuestionAnswers}
                    onAnswerComplete={handleAnswerComplete}
                    onStartFollowup={handleStartFollowup}
                    useSteppedInterface={true}
                    userAccessLevel={userAccessLevel}
                    story={story}
                  />
                </div>
              ) : (
                <div className="memory-stream-card text-center py-12">
                  <PlusIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">Select a question to start recording</p>
                  {questions.length === 0 && (
                    <p className="text-sm text-gray-400">
                      No questions available. Create a new story to get started.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
    </div>

    {/* Follow-up Interview Modal */}
    {followupMode && followupData && ReactDOM.createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" style={{ zIndex: 9999, position: 'fixed', isolation: 'isolate', WebkitTransform: 'translateZ(0)' }}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">AI Follow-up Interview</h2>
                <button
                  onClick={() => setFollowupMode(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">Original Question:</h3>
                <p className="text-blue-800 mb-3">{followupData.question.text}</p>
                <h4 className="font-medium text-blue-900 mb-2">Your Answer:</h4>
                <p className="text-blue-800">{followupData.transcription}</p>
              </div>

              <AIInterviewer
                storyId={followupData.storyId}
                answerId={followupData.answerId}
                transcript={followupData.transcription}
                originalQuestion={followupData.question.text}
                onComplete={handleFollowupComplete}
              />
            </div>
          </div>
        </div>,
        document.getElementById('modal-root')
      )}

      {/* Smart Question Wizard */}
      {showSmartQuestionWizard && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 999999, position: 'fixed', isolation: 'isolate', WebkitTransform: 'translateZ(0)' }}
        >
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <SmartAIQuestionGenerator
                story={story}
                onQuestionsUpdate={handleQuestionsUpdate}
                onCancel={() => setShowSmartQuestionWizard(false)}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* PDF Generation Modal */}
      {pdfGenerationModal.isOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 999999, position: 'fixed', isolation: 'isolate', WebkitTransform: 'translateZ(0)' }}
        >
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Export PDF</h2>
                <button
                  onClick={() => setPdfGenerationModal({ isOpen: false, storyId: null, storyTitle: null })}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">Story: {pdfGenerationModal.storyTitle}</h3>
              </div>
              
              <PDFGenerator
                storyId={pdfGenerationModal.storyId}
                storyTitle={pdfGenerationModal.storyTitle}
                onWizardOpen={() => {}}
                onWizardClose={() => setPdfGenerationModal({ isOpen: false, storyId: null, storyTitle: null })}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

    </>
  );
};
